import React from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { Control, Controller } from 'react-hook-form';
import HelperText from 'components/forms/HelperText';

// Older configs may carry a legacy "mode" (full / complement / disabled)
// instead of emit_core. It is read once to derive the flags and never written
// back; the Liquid block keeps its own read-only shim for metafields that still
// have it.
type ProductStructuredDataLegacyMode = 'full' | 'complement' | 'disabled';

type ProductStructuredDataConfig = {
  emit_core?: boolean;
  emit_images?: boolean;
  emit_additional_property?: boolean;
  emit_aggregate_rating?: boolean;
  emit_policy?: boolean;
  emit_shipping?: boolean;
};

type ProductStructuredDataFlagKey = keyof ProductStructuredDataConfig;

type ProductStructuredDataConfigControlProps = {
  control: Control;
  fieldName: string;
  readOnly?: boolean;
};

const LEGACY_MODES: ProductStructuredDataLegacyMode[] = ['full', 'complement', 'disabled'];

const FLAG_OPTIONS: Array<{
  key: ProductStructuredDataFlagKey;
  label: string;
  description: string;
}> = [
  {
    key: 'emit_core',
    label: 'Core product info',
    description: 'Include name, description, brand, SKU, price and availability. Turn off when the theme already outputs Product/ProductGroup structured data.',
  },
  {
    key: 'emit_images',
    label: 'Product images',
    description: 'Include the full product image array.',
  },
  {
    key: 'emit_additional_property',
    label: 'Attributes & options',
    description: 'Include variant options and product metafield attributes.',
  },
  {
    key: 'emit_aggregate_rating',
    label: 'Aggregate rating',
    description: 'Include star rating and review count from Microcommerce rating metafields. Review-app ratings are excluded on purpose: the review app emits its own schema.',
  },
  {
    key: 'emit_policy',
    label: 'Return policy',
    description: 'Include merchant return policy when the Shopify policy metafield is configured.',
  },
  {
    key: 'emit_shipping',
    label: 'Shipping details',
    description: 'Include shipping details when the Shopify shipping metafield is configured.',
  },
];

const normalizeConfig = (value: unknown): Required<ProductStructuredDataConfig> => {
  const raw = value && typeof value === 'object' && !Array.isArray(value)
    ? value as ProductStructuredDataConfig & { mode?: string }
    : {};
  const rawMode = typeof raw.mode === 'string' ? raw.mode.trim().toLowerCase() : '';
  const legacyMode = LEGACY_MODES.includes(rawMode as ProductStructuredDataLegacyMode)
    ? rawMode as ProductStructuredDataLegacyMode
    : 'full';

  // Legacy "disabled" configs carry no emit flags; they mean "emit nothing".
  if (legacyMode === 'disabled' && typeof raw.emit_core !== 'boolean') {
    return {
      emit_core: false,
      emit_images: false,
      emit_additional_property: false,
      emit_aggregate_rating: false,
      emit_policy: false,
      emit_shipping: false,
    };
  }

  const emitCore = typeof raw.emit_core === 'boolean'
    ? raw.emit_core
    : legacyMode !== 'complement';

  return {
    emit_core: emitCore,
    emit_images: raw.emit_images !== false,
    emit_additional_property: raw.emit_additional_property !== false,
    emit_aggregate_rating: raw.emit_aggregate_rating !== false,
    emit_policy: raw.emit_policy !== false,
    emit_shipping: raw.emit_shipping !== false,
  };
};

const ProductStructuredDataConfigControl = ({
  control,
  fieldName,
  readOnly = false,
}: ProductStructuredDataConfigControlProps) => (
  <Controller
    name={fieldName}
    control={control}
    render={({
      field: {
        onChange,
        value,
      },
      fieldState: {
        isTouched,
        error,
      },
    }) => {
      const config = normalizeConfig(value);
      const disabled = readOnly;
      const updateConfig = (patch: Partial<ProductStructuredDataConfig>) => {
        // Saves are flags-only: a legacy "mode" on the stored value is dropped here.
        onChange({
          ...config,
          ...patch,
        });
      };

      return (
        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Include in schema
            </Typography>
            <FormGroup>
              {FLAG_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.key}
                  control={(
                    <Checkbox
                      checked={Boolean(config[option.key])}
                      onChange={(event) => updateConfig({ [option.key]: event.target.checked })}
                      disabled={disabled}
                    />
                  )}
                  label={(
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  )}
                />
              ))}
            </FormGroup>
            <HelperText
              name={fieldName}
              error={isTouched && error?.message}
              description="Uncheck everything to emit no Product structured data."
            />
          </Box>
        </Stack>
      );
    }}
  />
);

ProductStructuredDataConfigControl.defaultProps = {
  readOnly: false,
};

export default observer(ProductStructuredDataConfigControl);
